import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./app/router";
import {
  ApiClient,
  ApiError,
  type Me,
  type OrganizationProfileEdit,
  type UserSettings,
  type WorldViewIndex,
} from "./lib/api";
import { AppProvider } from "./lib/AppContext";
import { type AppConfig, loadConfig } from "./lib/config";
import { clearTestSession, readTestSession } from "./lib/testSession";
import { useConversations } from "./lib/useConversations";
import { useLoaded } from "./lib/useLoaded";
import { useWorldViewActivity } from "./lib/useWorldViewActivity";
import { useRecentSources } from "./lib/useRecentSources";
import { useRecordings } from "./lib/useRecordings";
import { useRecordingMeetings } from "./lib/useRecordingMeetings";
import { useReview } from "./lib/useReview";
import { useSetup } from "./lib/useSetup";
import { ViewportProvider } from "./mobile/ViewportProvider";
import { createSupabaseClient, signInWithGitHub } from "./lib/supabase";
import { applyTheme, ThemeProvider, type Theme } from "./theme/ThemeProvider";

export default function App() {
  const configResult = useMemo(loadConfig, []);

  if (!configResult.ok) {
    return <ConfigurationProblem missing={configResult.missing} />;
  }

  return <AuthenticatedApp config={configResult.config} />;
}

function AuthenticatedApp({ config }: { config: AppConfig }) {
  const supabase = useMemo(() => createSupabaseClient(config), [config]);

  // A test session, when one has been put in local storage (SEC-6 removes this in M14).
  // Read once, so it cannot change under the app mid-run.
  const testSession = useMemo(() => readTestSession(), []);

  const api = useMemo(
    () =>
      new ApiClient(
        config.apiBaseUrl,
        testSession
          ? async () => testSession.accessToken
          : async () => (await supabase.auth.getSession()).data.session?.access_token ?? null,
      ),
    [config, supabase, testSession],
  );

  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    if (testSession) return;

    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setCheckingSession(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setCheckingSession(false);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [supabase, testSession]);

  if (testSession) {
    // No sign-out: a test session is ended by the banner's own control, which clears
    // the token this app was handed rather than a Supabase session it never had.
    return (
      <>
        <TestSessionBanner />
        <Shell api={api} />
      </>
    );
  }

  if (checkingSession) return <Centered>Checking your session…</Centered>;
  if (!session) return <SignIn supabase={supabase} />;

  return <Shell api={api} onSignOut={() => void supabase.auth.signOut()} />;
}

/**
 * Says out loud that this is not a real sign-in. Fixed rather than in the layout, so it
 * cannot change what is being tested; and it carries the way out, because a session
 * nobody can end is how a test account quietly becomes somebody's account.
 */
function TestSessionBanner() {
  return (
    <div
      className="fixed bottom-3 left-3 z-[9999] flex items-center gap-2 rounded-[var(--radius-full)] border border-[var(--warning)] px-3 py-1.5 text-[length:var(--text-xs)] text-[var(--text-primary)] shadow-[var(--shadow-lg)]"
      style={{ background: "color-mix(in oklch, var(--warning) 22%, var(--surface-2))" }}
    >
      <span className="font-semibold">Test session</span>
      <span className="text-[var(--text-secondary)]">not a real sign-in</span>
      <button
        type="button"
        onClick={() => {
          clearTestSession();
          window.location.reload();
        }}
        className="rounded-[var(--radius-full)] px-2 py-0.5 font-medium underline-offset-2 hover:underline"
      >
        End it
      </button>
    </div>
  );
}

function Shell({ api, onSignOut }: { api: ApiClient; onSignOut?: () => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .getMe()
      .then((result) => {
        if (cancelled) return;
        setMe(result);

        // The account's stored theme wins over this device's, so the choice follows
        // the user between machines. Applied directly rather than through state so
        // there is no flash of the previous theme.
        if (result.settings?.theme) applyTheme(result.settings.theme);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof ApiError
            ? `The Server refused the request (${cause.status}): ${cause.message}`
            : "The Server could not be reached.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const persistTheme = useCallback(
    (theme: Theme) => {
      // Best effort: the theme is already applied locally, and failing to record a
      // preference is not worth interrupting anybody over.
      void api.updateSettings({ theme }).catch(() => undefined);
    },
    [api],
  );

  if (error) {
    return (
      <Centered>
        <p className="max-w-md text-sm text-[var(--destructive)]">{error}</p>
      </Centered>
    );
  }

  if (!me) return <Centered>Loading your organization…</Centered>;

  return (
    <ThemeProvider onPersist={persistTheme}>
      <SignedIn api={api} me={me} onSignOut={onSignOut} />
    </ThemeProvider>
  );
}

/**
 * Everything past the front door. Split out so the hooks that need a loaded identity —
 * the recent-sources list above all — are not called before there is one.
 */
function SignedIn({ api, me, onSignOut }: { api: ApiClient; me: Me; onSignOut?: () => void }) {
  const { recentSources, refreshRecentSources } = useRecentSources(api);
  const { recordings, loading, problem, refresh } = useRecordings(api);
  const { meetingsByRecording, refresh: refreshRecordingMeetings } = useRecordingMeetings(api, recordings);
  const { setup, refresh: refreshSetup } = useSetup(api);
  const { counts: reviewCounts, refresh: refreshReview } = useReview(api);
  // UI-6, UI-8 — held here rather than in their pages because the navigator panel shows
  // the same sets, and two copies would disagree for a moment after every change. Not
  // polled: entities and people change only through this browser, which then refreshes.
  const {
    value: entities,
    loading: entitiesLoading,
    problem: entitiesProblem,
    refresh: refreshEntities,
  } = useLoaded(api, readEntities, [], "The entities could not be read.");

  const {
    value: people,
    loading: peopleLoading,
    problem: peopleProblem,
    refresh: refreshPeople,
  } = useLoaded(api, readPeople, [], "The people could not be read.");

  // The world view moves when synthesis finishes, which the activity hook reports below.
  const {
    value: worldView,
    loading: worldViewLoading,
    problem: worldViewProblem,
    refresh: refreshWorldView,
  } = useLoaded<WorldViewIndex | null>(api, readWorldView, null, "The world view could not be read.");

  const {
    activity: worldViewActivity,
    problem: worldViewActivityProblem,
    reload: refreshWorldViewActivity,
  } = useWorldViewActivity(api, refreshWorldView);

  const {
    conversations,
    loading: conversationsLoading,
    problem: conversationsProblem,
    available: chatAvailable,
    refresh: refreshConversations,
  } = useConversations(api);

  // ARCH-8 — kept here rather than read from `me` on every render, because Settings →
  // Advanced toggles it and the recording detail reads it: a copy loaded once at
  // start-up would mean turning developer mode on did nothing until a reload.
  const [settings, setSettings] = useState<UserSettings | null>(me.settings);

  const updateSettings = useCallback(
    async (patch: Partial<UserSettings>) => {
      setSettings(await api.updateSettings(patch));
    },
    [api],
  );

  // UI-18 — kept here for the same reason `settings` is: Account settings edits the
  // organization, and `me` is the answer to a question asked once at start-up.
  const [organization, setOrganization] = useState(me.organization);

  const updateOrganizationProfile = useCallback(
    async (profile: OrganizationProfileEdit) => {
      if (!organization) return;
      setOrganization(await api.updateOrganizationProfile(organization.id, profile));
    },
    [api, organization],
  );

  const context = useMemo(
    () => ({
      api,
      me,
      settings,
      updateSettings,
      organization,
      updateOrganizationProfile,
      setup,
      refreshSetup,
      reviewCounts,
      refreshReview,
      signOut: onSignOut,
      recentSources,
      refreshRecentSources,
      recordings,
      recordingsLoading: loading,
      recordingsProblem: problem,
      refreshRecordings: refresh,
      meetingsByRecording,
      refreshRecordingMeetings,
      entities,
      entitiesLoading,
      entitiesProblem,
      refreshEntities,
      people,
      peopleLoading,
      peopleProblem,
      refreshPeople,
      worldView,
      worldViewLoading,
      worldViewProblem,
      refreshWorldView,
      worldViewActivity,
      worldViewActivityProblem,
      refreshWorldViewActivity,
      conversations,
      conversationsLoading,
      conversationsProblem,
      chatAvailable,
      refreshConversations,
    }),
    [
      api, me, settings, updateSettings, organization, updateOrganizationProfile,
      setup, refreshSetup, reviewCounts, refreshReview,
      onSignOut, recentSources,
      refreshRecentSources, recordings, loading, problem, refresh,
      meetingsByRecording, refreshRecordingMeetings,
      entities, entitiesLoading, entitiesProblem, refreshEntities,
      people, peopleLoading, peopleProblem, refreshPeople,
      worldView, worldViewLoading, worldViewProblem, refreshWorldView,
      worldViewActivity, worldViewActivityProblem, refreshWorldViewActivity,
      conversations, conversationsLoading, conversationsProblem, chatAvailable, refreshConversations,
    ],
  );

  return (
    <AppProvider value={context}>
      {/* M12 — which shell is on screen. Above the router, because the answer changes what
          every route renders and a route should not have to ask twice. */}
      <ViewportProvider>
        <BrowserRouter>
          <AppRoutes initials={initialsFor(me)} />
        </BrowserRouter>
      </ViewportProvider>
    </AppProvider>
  );
}

const readEntities = (api: ApiClient) => api.listEntities();
const readPeople = (api: ApiClient) => api.listPeople();
const readWorldView = (api: ApiClient) => api.getWorldView();

/** Two letters for the app bar's avatar, from whatever the identity actually carries. */
function initialsFor(me: Me): string {
  const source = me.user.name ?? me.user.username ?? me.user.email ?? "";
  const words = source.split(/[\s._-]+/).filter(Boolean);

  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return "??";
}

function SignIn({ supabase }: { supabase: SupabaseClient }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGitHub(supabase);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Sign-in could not be started.");
      setBusy(false);
    }
  }, [supabase]);

  return (
    <Centered>
      <div className="w-full max-w-sm rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-2)] p-8">
        <h1 className="text-[length:var(--text-xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Factorium
        </h1>
        <p className="mt-2 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Sign in to reach your sources, insights and world view.
        </p>

        <button
          type="button"
          onClick={() => void start()}
          disabled={busy}
          className="mt-6 w-full rounded-[var(--radius-md)] bg-[var(--primary)] px-4 py-2.5 text-[length:var(--text-md)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "Redirecting…" : "Continue with GitHub"}
        </button>

        {error && <p className="mt-4 text-[length:var(--text-sm)] text-[var(--destructive)]">{error}</p>}
      </div>
    </Centered>
  );
}

function ConfigurationProblem({ missing }: { missing: string[] }) {
  return (
    <Centered>
      <div className="max-w-md rounded-[var(--radius-xl)] border border-[var(--warning)] bg-[var(--surface-2)] p-6">
        <h1 className="text-[length:var(--text-lg)] font-semibold" style={{ color: "var(--warning)" }}>
          This deployment is not configured
        </h1>
        <p className="mt-2 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          The following values are missing from the container environment:
        </p>
        <ul className="mt-3 list-inside list-disc text-[length:var(--text-sm)] text-[var(--text-primary)]">
          {missing.map((name) => (
            <li key={name}>
              <code>{name}</code>
            </li>
          ))}
        </ul>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--sidebar)] p-6 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
      {children}
    </div>
  );
}
